import React from 'react';
import { Star, Users, ExternalLink, Heart, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot } from '@/types/Bot';
import { ComparisonButton } from './ComparisonButton';

interface BotCardProps {
  bot: Bot;
  onBotClick?: (bot: Bot) => void;
  onToggleFavorite?: (botId: string) => void;
  isFavorite?: boolean;
  isInComparison?: boolean;
  canAddToComparison?: boolean;
  onAddToComparison?: (bot: Bot) => void;
  onRemoveFromComparison?: (botId: string) => void;
}

export const BotCard: React.FC<BotCardProps> = ({ 
  bot, onBotClick, onToggleFavorite, isFavorite = false,
  isInComparison = false, canAddToComparison = true,
  onAddToComparison, onRemoveFromComparison
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('[data-no-navigate]')) return;
    onBotClick?.(bot);
  };

  const features = bot.key_features && bot.key_features.length > 0 ? bot.key_features : bot.features;

  return (
    <Card 
      className="group relative overflow-hidden border-border bg-card
                 transition-all duration-200 cursor-pointer
                 hover:shadow-md hover:-translate-y-0.5"
      onClick={handleCardClick}
    >
      {/* Badges: New / Trending */}
      {bot.isNew && (
        <Badge className="absolute top-3 left-3 z-10 bg-foreground text-background text-xs">New</Badge>
      )}
      {bot.isTrending && !bot.isNew && (
        <Badge variant="outline" className="absolute top-3 left-3 z-10 text-xs gap-1">
          <TrendingUp className="w-3 h-3" /> Trending
        </Badge>
      )}

      {/* Favorite button */}
      {onToggleFavorite && (
        <Button
          variant="ghost" size="sm" data-no-navigate
          className="absolute top-3 right-3 z-10 h-8 w-8 p-0 hover:bg-muted"
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(bot.id); }}
          aria-label={isFavorite ? `Remove ${bot.name} from favorites` : `Add ${bot.name} to favorites`}
        >
          <Heart className={`w-4 h-4 transition-colors ${isFavorite ? 'fill-foreground text-foreground' : 'text-muted-foreground'}`} />
        </Button>
      )}

      {/* Emoji avatar */}
      <div className={`absolute ${bot.isNew || bot.isTrending ? 'top-12' : 'top-3'} left-3 z-10`}>
        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-xl">
          {(bot as any).emoji || '🤖'}
        </div>
      </div>
      
      <CardHeader className={`pb-2 ${bot.isNew || bot.isTrending ? 'pt-14' : 'pt-5'} pl-16`}>
        <CardTitle className="text-base font-semibold pr-8 group-hover:text-accent transition-colors">
          {bot.name}
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs font-medium">{bot.price}</Badge>
          <Badge variant="secondary" className="capitalize text-xs">{bot.category}</Badge>
          {(bot as any).featured && (
            <Badge className="text-xs gap-1">
              <Sparkles className="w-3 h-3" /> Featured
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 fill-foreground text-foreground" />
            <span className="font-medium text-foreground">{bot.rating}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            <span>{bot.reviewCount.toLocaleString()}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 pt-0">
        <CardDescription className="leading-relaxed line-clamp-2 text-sm">
          {bot.description}
        </CardDescription>

        {features.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {features.slice(0, 3).map((feature, index) => (
              <Badge key={index} variant="outline" className="text-xs font-normal">{feature}</Badge>
            ))}
            {features.length > 3 && (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">+{features.length - 3}</Badge>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          {(onAddToComparison || onRemoveFromComparison) && (
            <ComparisonButton
              bot={bot} isInComparison={isInComparison} canAddMore={canAddToComparison}
              onAddToComparison={onAddToComparison!} onRemoveFromComparison={onRemoveFromComparison!}
              variant="outline" size="sm"
            />
          )}
          <div className="flex-1" />
          <Button size="sm" variant="ghost" className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => window.open(bot.website, '_blank')}>
            <ExternalLink className="w-3 h-3 mr-1" /> Visit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
